import { UiButtonDirective, UiControlDirective } from '@template-ui/directives';
import { Component, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  BlockRegistry,
  WorkspaceBlockHostComponent,
  WorkspaceContextSession,
} from '@template-ui/blocks';
import type {
  BlockHost,
  BlockManifest,
  BlockRegion,
  WorkspaceBlock,
} from '@template-ui/blocks';

const hosts: BlockHost[] = [
  'step-shell',
  'case-context',
  'dashboard-expansion',
];
function manifest(code: string, name: string, reads: boolean): BlockManifest {
  return {
    provider: 'preview',
    code,
    version: 1,
    name,
    description: 'Local synthetic layout preview.',
    category: 'Preview',
    keywords: [],
    contextNeeds: ['personId', 'hearingId', 'proceedingId'],
    reads: reads
      ? [
          {
            code: 'preview.record',
            version: 1,
            parameters: { person: 'personId' },
            dataContract: { code: 'preview.record-data', version: 1 },
          },
        ]
      : [],
    settingsSchema: { code: 'preview.settings', version: 1, fields: {} },
    placements: hosts.flatMap((host) =>
      (['main', 'supporting'] as BlockRegion[]).map((region) => ({
        host,
        region,
      })),
    ),
    layout: {
      widths: ['full', 'half'],
      minimumWidthPx: 180,
      minimumHeightPx: 160,
      responsive: 'stack-in-order',
    },
    instances: 'single-per-workspace',
  };
}
@Component({
  selector: 'phms-workspace-host-preview',
  standalone: true,
  imports: [
    UiButtonDirective,
    UiControlDirective,
    FormsModule,
    WorkspaceBlockHostComponent,
  ],
  template: `<details (toggle)="open = $any($event.target).open">
    <summary>Workspace layout preview</summary>
    @if (open) {
      <p class="preview-caption">
        Synthetic records only. This layout preview does not save data or change
        the published workflow.
      </p>
      <div class="preview-controls">
        <label
          >Preview surface
          <select uiControl [(ngModel)]="host" (ngModelChange)="configure()">
            <option value="step-shell">Step workspace</option>
            <option value="case-context">Case page</option>
            <option value="dashboard-expansion">Dashboard expansion</option>
          </select></label
        >
        <label
          ><input
            uiControl
            type="checkbox"
            [(ngModel)]="custom"
            (ngModelChange)="configure()"
          />Custom main interaction</label
        >
      </div>
      <ez-workspace-block-host
        [registry]="registry"
        [session]="session"
        [host]="host"
        [blocks]="blocks"
        [mainInteraction]="mainInteraction"
      >
        <div workspaceSummary class="preview-summary">
          <strong>Initial hearing — sample workspace</strong
          ><span>Person 1201 · Hearing 4021</span>
        </div>
        <div workspaceActions class="preview-actions">
          <button uiButton type="button" (click)="session.invalidate()">
            Refresh preview data</button
          ><button uiButton type="button" disabled>Submit decision</button>
        </div>
        <p workspaceRequirements class="preview-requirements">
          Requirement status: preview only. No completion rule is evaluated
          here.
        </p>
      </ez-workspace-block-host>
    }
  </details>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
        padding: 16px;
        color: var(--custom-text-secondary-color);
      }
      details {
        min-width: 0;
        border: 1px solid var(--custom-border-subtle-color);
        border-radius: 6px;
        padding: 16px;
      }
      summary {
        cursor: pointer;
        font-weight: 600;
      }
      summary:focus-visible {
        outline: 2px solid var(--custom-focus-ring-color);
        outline-offset: 2px;
      }
      .preview-caption {
        margin: 12px 0;
      }
      .preview-controls,
      .preview-actions {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        align-items: center;
        margin: 12px 0;
      }
      label {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px;
      }
      select,
      button {
        max-width: 100%;
      }

      .preview-summary {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 8px;
        padding: 16px 0 8px;
        border-top: 1px solid var(--custom-border-subtle-color);
      }
      .preview-requirements {
        padding: 10px 12px;
        border-left: 3px solid var(--custom-status-info-border-color);
        background: var(--custom-status-info-background-color);
        color: var(--custom-status-info-text-color);
        margin: 0 0 16px;
      }
      @media (max-width: 600px) {
        :host,
        details {
          padding: 8px;
        }
      }
    `,
  ],
})
export class WorkspaceHostPreviewComponent implements OnDestroy {
  open = false;
  host: BlockHost = 'step-shell';
  custom = false;
  readonly registry = new BlockRegistry();
  session!: WorkspaceContextSession;
  blocks: readonly WorkspaceBlock[] = [];
  mainInteraction: WorkspaceBlock | null = null;
  constructor() {
    this.registry.register({
      manifest: manifest('record', 'Record overview', true),
      load: () =>
        import('./workspace-preview-panels').then(
          (m) => m.WorkspacePreviewRecordPanel,
        ),
    });
    this.registry.register({
      manifest: manifest('note', 'Working notes', false),
      load: () =>
        import('./workspace-preview-panels').then(
          (m) => m.WorkspacePreviewNotePanel,
        ),
    });
    this.registry.register({
      manifest: manifest('source', 'Source details', true),
      load: () =>
        import('./workspace-preview-panels').then(
          (m) => m.WorkspacePreviewSourcePanel,
        ),
    });
    this.configure();
  }
  configure(): void {
    this.session?.dispose();
    this.session = new WorkspaceContextSession(
      {
        version: 1,
        domain: { personId: 1201, hearingId: 4021, proceedingId: 5021 },
        ...(this.host === 'step-shell'
          ? {
              workflow: {
                workflowInstanceId: 6021,
                definitionVersionId: 1,
                stepOccurrenceId: 7021,
                nodeId: 'prepare',
                occurrenceNumber: 1,
              },
            }
          : {}),
      },
      {
        read: async () => ({
          data: { synthetic: true },
          sources: [
            {
              provider: 'preview',
              contract: { code: 'preview.record-data', version: 1 },
              recordKey: 'synthetic:1201',
              sourceVersion: 'fixture-1',
              observedAtUtc: new Date().toISOString(),
              observation: 'client-receipt',
              consistency: 'independent-read',
            },
          ],
        }),
      },
    );
    const block = (
      code: string,
      region: BlockRegion,
      width: 'full' | 'half',
    ): WorkspaceBlock => ({
      instanceId: code,
      block: { provider: 'preview', code, version: 1 },
      settingsVersion: 1,
      settings: {},
      placement: { host: this.host, region },
      width,
    });
    this.blocks = [
      block('record', 'main', 'half'),
      ...(this.custom ? [] : [block('note', 'main', 'half')]),
      block('source', 'supporting', 'full'),
    ];
    this.mainInteraction = this.custom ? block('note', 'main', 'full') : null;
  }
  ngOnDestroy(): void {
    this.session.dispose();
  }
}
